import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Download, Trash2, CalendarClock, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useRepFilter } from "@/context/RepFilterContext";

const DAY_INDEX = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ProviderAvailabilityNudge({ facilityId, dateIso }) {
  const [facility, setFacility] = useState(null);
  useEffect(() => {
    if (!facilityId) { setFacility(null); return; }
    api.get(`/facilities/${facilityId}`).then(({ data }) => setFacility(data)).catch(() => setFacility(null));
  }, [facilityId]);

  const nudge = useMemo(() => {
    if (!facility || !dateIso) return null;
    const d = new Date(`${dateIso}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    const dayLabel = DAY_INDEX[d.getDay()];
    const pm = facility.providers_map || {};
    const attached = [];
    Object.keys(pm).forEach((spec) => {
      (pm[spec] || []).forEach((p) => attached.push({ ...p, specialty: spec }));
    });
    const activeAttached = attached.filter((p) => p.active !== false);
    if (activeAttached.length === 0) return null;
    const available = activeAttached.filter((p) => (p.available_days || []).includes(dayLabel));
    return { dayLabel, available, total: activeAttached.length };
  }, [facility, dateIso]);

  if (!nudge) return null;
  const someAvailable = nudge.available.length > 0;
  return (
    <div
      data-testid="visit-availability-nudge"
      className={`text-xs rounded-md p-2.5 border ${someAvailable
        ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950 dark:border-emerald-800 dark:text-emerald-200"
        : "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200"}`}
    >
      <div className="flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-widest">
        {someAvailable ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
        {nudge.dayLabel} availability
      </div>
      {someAvailable ? (
        <div className="mt-1">
          <b>{nudge.available.length}</b> of {nudge.total} attached provider{nudge.total === 1 ? "" : "s"} available on {nudge.dayLabel}:
          <div className="mt-1 flex flex-wrap gap-1">
            {nudge.available.map((p, i) => (
              <span key={i} className="rounded bg-white/70 border border-emerald-200 px-1.5 py-0.5 text-[11px]">{p.name} <span className="text-emerald-700/70">· {p.specialty}</span></span>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-1">
          None of the {nudge.total} attached provider{nudge.total === 1 ? "" : "s"} lists <b>{nudge.dayLabel}</b> as an available day. Consider another date, or check the provider roster.
        </div>
      )}
    </div>
  );
}

export default function Visits() {
  const [rows, setRows] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ facility_id: "", visit_date: new Date().toISOString().slice(0, 10), visit_type: "facility_visit", purpose: "", outcome: "", attendees: "", notes: "" });

  const { repId, reps } = useRepFilter();
  const repName = reps.find(r => r.user_id === repId)?.name;

  const load = () => api.get("/visits").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/facilities").then(({ data }) => setFacilities(data)); }, []);
  const visibleRows = useMemo(() => (repId === "all" ? rows : rows.filter(r => r.created_by === repId)), [rows, repId]);

  const submit = async () => {
    try {
      await api.post("/visits", form);
      toast.success("Visit logged");
      setOpen(false); setForm({ ...form, purpose: "", outcome: "", attendees: "", notes: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this visit?")) return;
    try { await api.delete(`/visits/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("aceh_token") || "";
    const resp = await fetch(`${API}/visits/export.csv`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "visits.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="visits-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Field activity</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black">Visits</h1>
          <p className="text-slate-500 mt-1 text-sm">Every touchpoint with a facility.{repId !== "all" && repName && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-mono-plex uppercase tracking-wider" data-testid="visits-rep-chip">{repName}</span>}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="visits-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="visit-new-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> Log visit</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New visit</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Facility</Label>
                  <Select value={form.facility_id} onValueChange={v => setForm({ ...form, facility_id: v })}>
                    <SelectTrigger data-testid="visit-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
                    <SelectContent>{facilities.map(f => <SelectItem key={f.facility_id} value={f.facility_id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Date</Label><Input type="date" data-testid="visit-date" value={form.visit_date} onChange={e => setForm({ ...form, visit_date: e.target.value })} /></div>
                <ProviderAvailabilityNudge facilityId={form.facility_id} dateIso={form.visit_date} />
                <div><Label>Visit type</Label>
                  <Select value={form.visit_type} onValueChange={v => setForm({ ...form, visit_type: v })}>
                    <SelectTrigger data-testid="visit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facility_visit" data-testid="visit-type-facility">Facility visit · counted as Active or Prospect/Churn by facility status</SelectItem>
                      <SelectItem value="provider_meeting" data-testid="visit-type-provider">Provider meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Purpose</Label><Input data-testid="visit-purpose" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} /></div>
                <div><Label>Outcome</Label><Input value={form.outcome} onChange={e => setForm({ ...form, outcome: e.target.value })} /></div>
                <div><Label>Attendees</Label><Input value={form.attendees} onChange={e => setForm({ ...form, attendees: e.target.value })} /></div>
                <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="visit-save" className="bg-slate-900 hover:bg-slate-800">Log visit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
            <tr><th className="p-3">Date</th><th className="p-3">Facility</th><th className="p-3">Purpose</th><th className="p-3">By</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-slate-400" data-testid="visits-empty">{repId === "all" ? "No visits yet." : `No visits logged by ${repName || "this rep"}.`}</td></tr>}
            {visibleRows.map(r => (
              <tr key={r.visit_id} className="border-t border-slate-100 row-enter">
                <td className="p-3 font-mono-plex">{r.visit_date}</td>
                <td className="p-3 font-medium"><Link to={`/facilities/${r.facility_id}`} className="text-slate-900 hover:text-indigo-700 hover:underline inline-flex items-center gap-1" data-testid={`visit-facility-link-${r.visit_id}`}>{r.facility_name} <ExternalLink className="w-3 h-3 text-slate-400" /></Link>{r.visit_type === "provider_meeting" && <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-mono-plex" data-testid={`visit-type-badge-${r.visit_id}`}>Provider mtg</span>}</td>
                <td className="p-3 text-slate-700">{r.purpose}<div className="text-xs text-slate-400 line-clamp-1">{r.notes}</div></td>
                <td className="p-3 text-xs text-slate-500">{r.created_by_name}</td>
                <td className="p-3 text-right"><Button size="icon" variant="ghost" onClick={() => del(r.visit_id)} data-testid={`visit-del-${r.visit_id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
